import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import Icon from '../components/Icon';

// Ajanın SCHEMA_SPEC'i ile birebir aynı olmalı (agent/src/db/schema-detect.js).
const GROUPS = [
  {
    key: 'account',
    tables: [
      { name: 'account', required: true },
      { name: 'login_log' },
    ],
  },
  {
    key: 'player',
    tables: [
      { name: 'player', required: true },
      { name: 'guild' }, { name: 'guild_member' }, { name: 'safebox' },
      { name: 'marriage' }, { name: 'highscore' }, { name: 'exchange_yang' },
      { name: 'myshop_price' }, { name: 'offshop_item' },
    ],
  },
  {
    key: 'common',
    tables: [
      { name: 'item', required: true },
      { name: 'item_proto' }, { name: 'mob_proto' },
    ],
  },
  {
    key: 'log',
    tables: [
      { name: 'item_log' }, { name: 'money_log' }, { name: 'login_log2' },
      { name: 'level_log' }, { name: 'hack_log' }, { name: 'cube_log' },
      { name: 'refine_log' }, { name: 'boss_log' },
    ],
  },
];

// Metrik açısından en kritik kolonlar — elle override girişi.
const COL_FIELDS = {
  'player:player': [['gold', 'Yang / para kolonu'], ['level', 'Seviye kolonu']],
  'account:account': [['last_play', 'Son giriş kolonu'], ['create_time', 'Kayıt tarihi kolonu'], ['cash', 'EP / cash kolonu']],
  'log:money_log': [['time', 'Zaman kolonu'], ['gold', 'Tutar kolonu']],
};

const selStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--r-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-1)',
  color: 'var(--text)', fontSize: 14,
};

export default function SchemaConfirm() {
  const { t } = useTranslation();
  const { reloadLicense, activeServerKey } = useAuth();
  const srv = activeServerKey || undefined;

  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [ov, setOv] = useState({}); // düzenlenen override
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    api.getSchemaConfirmation(srv)
      .then((r) => {
        setRec(r.schemaConfirmation);
        setOv(r.schemaConfirmation?.mapping ? clone(r.schemaConfirmation.mapping) : {});
      })
      .finally(() => setLoading(false));
  }, []);

  const candidates = rec?.candidates || {};
  const databases = candidates.databases || [];
  const tablesByDb = candidates.tablesByDb || {};
  const status = rec?.status || 'ok';

  const guessDb = (gk) => candidates[gk]?.matches?.[0]?.db || '';
  const effDb = (gk) => ov[gk]?.db || guessDb(gk) || '';
  const tablesFor = (gk) => tablesByDb[effDb(gk)] || [];

  const setGroupDb = (gk, db) =>
    setOv((m) => ({ ...m, [gk]: { ...(m[gk] || {}), db: db || undefined } }));

  const setTable = (gk, tName, val) =>
    setOv((m) => {
      const g = { ...(m[gk] || {}) };
      const tables = { ...(g.tables || {}) };
      if (!val) delete tables[tName];
      else tables[tName] = val;
      g.tables = tables;
      return { ...m, [gk]: g };
    });

  const setCol = (gk, tName, logicalCol, val) =>
    setOv((m) => {
      const g = { ...(m[gk] || {}) };
      const columns = { ...(g.columns || {}) };
      const forT = { ...(columns[tName] || {}) };
      if (!val.trim()) delete forT[logicalCol];
      else forT[logicalCol] = val.trim();
      if (Object.keys(forT).length) columns[tName] = forT;
      else delete columns[tName];
      g.columns = columns;
      return { ...m, [gk]: g };
    });

  const isEmpty = useMemo(() => {
    for (const g of Object.values(ov)) {
      if (g?.db) return false;
      if (g?.tables && Object.keys(g.tables).length) return false;
      if (g?.columns && Object.keys(g.columns).length) return false;
    }
    return true;
  }, [ov]);

  const submit = async (payload) => {
    setErr(''); setSaved(''); setBusy(true);
    try {
      const r = await api.submitSchemaConfirmation(payload, srv);
      setRec((prev) => ({ ...(prev || {}), status: r.schemaConfirmation?.status || 'ok', mapping: r.schemaConfirmation?.mapping || null }));
      if (!r.schemaConfirmation) setOv({});
      setSaved(payload == null ? t('schema.reset_done') : t('schema.done'));
      await reloadLicense();
    } catch (e) {
      setErr(e.message || t('schema.err_incomplete'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('schema.title')} crumb={t('onboarding.title')} heading={false}>
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  if (!rec) {
    return (
      <DashboardLayout title={t('schema.title')} crumb={t('onboarding.title')} heading={false}>
        <div className="empty-state">
          <Icon name="hash" size={26} style={{ color: 'var(--text-3)' }} />
          <h3 style={{ marginTop: 12 }}>{t('schema.no_agent_title')}</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 13, maxWidth: 420 }}>{t('schema.no_agent_desc')}</p>
          <Link to="/onboarding" className="btn btn-subtle btn-sm" style={{ marginTop: 'var(--sp-4)' }}>
            {t('schema.back_to_onboarding')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const renderTableRow = (gk, tbl) => {
    const opts = tablesFor(gk);
    const cur = ov[gk]?.tables?.[tbl.name] || '';
    const curStr = typeof cur === 'string' ? cur : cur?.name || '';
    const colFields = COL_FIELDS[`${gk}:${tbl.name}`];
    const colValActive = curStr && curStr !== '__absent__';
    return (
      <div key={tbl.name} style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 5 }}>
          <code>{tbl.name}</code>{' '}
          <span style={{ color: tbl.required ? 'var(--warn)' : 'var(--text-3)' }}>
            ({tbl.required ? t('schema.required') : t('schema.optional')})
          </span>
        </label>
        <select value={curStr} onChange={(e) => setTable(gk, tbl.name, e.target.value)} style={selStyle}>
          <option value="">{t('schema.auto')}</option>
          <option value="__absent__">{t('schema.absent')}</option>
          {curStr && curStr !== '__absent__' && !opts.includes(curStr) && (
            <option value={curStr}>{curStr}</option>
          )}
          {opts.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
        </select>
        {colFields && colValActive && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 8 }}>
            {colFields.map(([logical, hint]) => (
              <input
                key={logical}
                type="text"
                placeholder={hint}
                value={ov[gk]?.columns?.[tbl.name]?.[logical] || ''}
                onChange={(e) => setCol(gk, tbl.name, logical, e.target.value)}
                style={{ ...selStyle, fontSize: 13 }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g) => {
    const dbSel = ov[g.key]?.db || '';
    return (
      <div className="card" key={g.key} style={{ marginBottom: 'var(--sp-4)' }}>
        <h3 style={{ fontSize: '0.98rem', marginBottom: 12 }}>{t(`schema.group_${g.key}`)}</h3>
        <div className="field">
          <label>{t('schema.database')}</label>
          <select value={dbSel} onChange={(e) => setGroupDb(g.key, e.target.value)} style={selStyle}>
            <option value="">{t('schema.auto')}{guessDb(g.key) ? ` (${guessDb(g.key)})` : ''}</option>
            {databases.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        {g.tables.filter((x) => x.required).map((tbl) => renderTableRow(g.key, tbl))}
        {g.tables.some((x) => !x.required) && (
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-3)' }}>
              {t('schema.optional_tables')} ({g.tables.filter((x) => !x.required).length})
            </summary>
            <div style={{ marginTop: 12 }}>
              {g.tables.filter((x) => !x.required).map((tbl) => renderTableRow(g.key, tbl))}
            </div>
          </details>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout title={t('schema.title')} crumb={t('onboarding.title')} heading={false}>
      <h1 className="page-title">{t('schema.title')}</h1>
      <p className="page-sub">{t('schema.subtitle2')}</p>

      <div className="card" style={{
        marginBottom: 'var(--sp-4)', display: 'flex', gap: 10, alignItems: 'flex-start',
        borderColor: status === 'pending' ? 'var(--border-accent)' : 'var(--border)',
        background: status === 'pending' ? 'var(--warn-soft)' : undefined,
      }}>
        <Icon
          name={status === 'pending' ? 'alert-triangle' : status === 'confirmed' ? 'check' : 'database'}
          size={16}
          style={{ color: status === 'pending' ? 'var(--warn)' : status === 'confirmed' ? 'var(--ok)' : 'var(--text-3)', flexShrink: 0, marginTop: 2 }}
        />
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
          {status === 'pending' ? t('schema.state_pending')
            : status === 'confirmed' ? t('schema.state_confirmed')
            : t('schema.state_auto')}
        </p>
      </div>

      <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 20 }}>
        {t('schema.detected', { list: databases.join(', ') || '—' })}
      </p>

      {GROUPS.map(renderGroup)}

      {err && <div className="form-error"><Icon name="alert-triangle" size={14} /> {err}</div>}
      {saved && <div className="badge ok" style={{ marginBottom: 12 }}><Icon name="check" size={12} /> {saved}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        <button className="btn" onClick={() => submit(isEmpty ? null : clean(ov))} disabled={busy}>
          {busy ? t('schema.submitting') : t('schema.save')}
        </button>
        {status === 'confirmed' && (
          <button className="btn btn-subtle" onClick={() => submit(null)} disabled={busy}>
            {t('schema.reset')}
          </button>
        )}
        <Link to="/onboarding" className="btn btn-subtle">{t('schema.back_to_onboarding')}</Link>
      </div>
      <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 14 }}>{t('schema.save_note')}</p>
    </DashboardLayout>
  );
}

function clone(o) { return JSON.parse(JSON.stringify(o)); }

// boş grup/alanları at
function clean(ov) {
  const out = {};
  for (const [gk, g] of Object.entries(ov)) {
    const o = {};
    if (g.db) o.db = g.db;
    if (g.tables && Object.keys(g.tables).length) o.tables = g.tables;
    if (g.columns && Object.keys(g.columns).length) o.columns = g.columns;
    if (Object.keys(o).length) out[gk] = o;
  }
  return out;
}
